import React, { DragEvent, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Copy, Check, X } from "lucide-react";
import { AppLayout } from "../components/layout/AppLayout";
import type { ProjectSummary } from "../types/dashboard";
import type {
    ProjectDocument,
    ProjectDocumentsApiResponse,
} from "../types/documents";
import type { SourcesTabKey } from "./projectSources/constants";
import {
    deriveTitleFromFileName,
    filterDocumentsForTab,
    mapTabToHelperText,
    mapTabToLabel,
    tabToDocumentTypes,
} from "./projectSources/constants";
import { useOnboarding, OnboardingStepId } from "../context/OnboardingContext";
import { useAuth } from "../auth/AuthContext";
import { api } from "../lib/apiClient";
import { hasApiBaseUrl } from "../lib/orgRuntime";


type ProjectsApiResponse = {
    ok: boolean;
    projects?: ProjectSummary[];
    error?: string;
};

type CreateProjectApiResponse = {
    ok: boolean;
    project?: {
        id: string;
        name: string;
        emailSlug?: string | null;
        status?: string | null;
        createdAt?: string | null;
    };
    error?: string;
};

const EMAIL_SLUG_MAX_LENGTH = 40;
const EMAIL_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

function normalizeEmailSlug(value: string): string {
    return value
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .replace(/-{2,}/g, "-")
        .slice(0, EMAIL_SLUG_MAX_LENGTH)
        .replace(/-+$/g, "");
}

function suggestEmailSlug(projectName: string): string {
    const normalized = normalizeEmailSlug(projectName);
    const words = normalized.split("-").filter(Boolean);

    if (words.length > 1) {
        const meaningfulWords = words.filter((word) => !["the", "a", "an", "of"].includes(word));
        const selected = meaningfulWords.length > 0 ? meaningfulWords : words;
        const first = selected[0] ?? "project";
        const numberWords = selected.filter((word) => /^\d+$/.test(word));
        const second = selected.find((word) => word !== first && !/^\d+$/.test(word));

        return normalizeEmailSlug([first, second, ...numberWords].filter(Boolean).join("-"));
    }

    if (!normalized) return "project";

    return normalized.length >= 3 ? normalized : normalizeEmailSlug(`${normalized}-project`);
}

export const ProjectSourcesPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [projects, setProjects] = useState<ProjectSummary[]>([]);
    const [projectsError, setProjectsError] = useState<string | null>(null);
    const [isCreatingProject, setIsCreatingProject] = useState(false);
    const [isProjectSetupOpen, setIsProjectSetupOpen] = useState(false);
    const [projectNameDraft, setProjectNameDraft] = useState("");
    const [projectEmailSlugDraft, setProjectEmailSlugDraft] = useState("");
    const [projectSlugEdited, setProjectSlugEdited] = useState(false);
    const [projectSetupError, setProjectSetupError] = useState<string | null>(null);
    const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);

    const [activeTab, setActiveTab] = useState<SourcesTabKey>("scopes");

    const [documents, setDocuments] = useState<ProjectDocument[]>([]);
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
    const [documentsError, setDocumentsError] = useState<string | null>(null);

    const [isDragging, setIsDragging] = useState(false);
    const [isIngesting, setIsIngesting] = useState(false);
    const [ingestError, setIngestError] = useState<string | null>(null);

    const [isRunningAnalysis, setIsRunningAnalysis] = useState(false);
    const [analysisError, setAnalysisError] = useState<string | null>(null);

    // NEW: track exclude/include errors
    const [exclusionError, setExclusionError] = useState<string | null>(null);

    // Email ingestion address
    const [ingestAddress, setIngestAddress] = useState<string | null>(null);
    const [copiedAddress, setCopiedAddress] = useState(false);

    // NEW: onboarding integration
    const { markStepComplete } = useOnboarding();
    const { enrollmentError, orgId } = useAuth();
    const canUseOrgScopedApi = hasApiBaseUrl && !!orgId;

    // Mark "Go to Sources" as complete whenever this page is visited
    useEffect(() => {
        markStepComplete("goToSources" as OnboardingStepId);
    }, [markStepComplete]);



    const handleProjectChange = (projectId: string) => {
        console.log("[ProjectSourcesPage] handleProjectChange called with projectId:", projectId);
        setCurrentProjectId(projectId);
        setSearchParams({ projectId, tab: activeTab });
        setDocuments([]);
        setDocumentsError(null);
        setIngestError(null);
        setAnalysisError(null);
        setExclusionError(null);
        setIngestAddress(null);
        setCopiedAddress(false);

        // Onboarding: project selected
        console.log("[ProjectSourcesPage] Calling markStepComplete('project')");
        markStepComplete("project");
    };

    const loadProjects = async () => {
        // If API isn't wired yet, fall back to demo project
        if (!hasApiBaseUrl || !orgId) {
            setProjects([]);
            return;
        }

        setProjectsError(null);

        try {
            const json: ProjectsApiResponse = await api.get("/projects");

            if (!json.ok) {
                throw new Error(json.error ?? "Failed to load projects");
            }

            const loaded = json.projects ?? [];

            setProjects(loaded);
            // Do NOT auto-select a project here either.
        } catch (err: any) {
            setProjectsError(err?.message ?? "Failed to load projects");
        }
    };

    useEffect(() => {
        void loadProjects();
    }, [canUseOrgScopedApi, orgId]);

    // Set currentProjectId from URL params after projects are loaded
    useEffect(() => {
        if (projects.length > 0 && !currentProjectId) {
            const projectIdFromUrl = searchParams.get("projectId");
            if (projectIdFromUrl && projects.some(p => p.id === projectIdFromUrl)) {
                // If there's a valid projectId in URL, use it but DON'T mark step complete
                // (only mark complete when user actively selects)
                setCurrentProjectId(projectIdFromUrl);
            }
        }
    }, [projects, currentProjectId, searchParams]);

    const loadDocuments = async (projectId: string) => {
        if (!canUseOrgScopedApi) return;
        setIsLoadingDocuments(true);
        setDocumentsError(null);
        setExclusionError(null);

        try {
            const json: ProjectDocumentsApiResponse = await api.get(
                `/projects/${projectId}/documents`,
            );

            if (!json.ok) {
                throw new Error(
                    (json as any)?.error ?? "Failed to load project documents",
                );
            }

            setDocuments(json.documents ?? []);
        } catch (err: any) {
            setDocumentsError(err?.message || "Failed to load project documents");
        } finally {
            setIsLoadingDocuments(false);
        }
    };

    useEffect(() => {
        if (currentProjectId) {
            void loadDocuments(currentProjectId);
        }
    }, [currentProjectId, canUseOrgScopedApi]);

    // Fetch ingest address when project changes
    useEffect(() => {
        if (!currentProjectId || !canUseOrgScopedApi) {
            setIngestAddress(null);
            return;
        }
        (async () => {
            try {
                const json = await api.get(
                    `/projects/${currentProjectId}/email-ingestion`,
                );
                setIngestAddress(json?.ingestRoute?.address ?? null);
            } catch {
                setIngestAddress(null);
            }
        })();
    }, [currentProjectId, canUseOrgScopedApi]);

    const handleCopyAddress = async () => {
        if (!ingestAddress) return;
        try {
            await navigator.clipboard.writeText(ingestAddress);
            setCopiedAddress(true);
            setTimeout(() => setCopiedAddress(false), 2000);
        } catch (err) {
            console.error("Failed to copy address:", err);
        }
    };

    useEffect(() => {
        const tab = searchParams.get("tab") as SourcesTabKey | null;
        if (tab && ["scopes", "project_docs", "emails_notes"].includes(tab)) {
            setActiveTab(tab);
        }
    }, [searchParams]);

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        if (!currentProjectId) return;

        const files = e.dataTransfer.files;
        if (!files || files.length === 0) return;

        void ingestFileForCurrentTab(files[0], currentProjectId, activeTab);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!currentProjectId) return;
        const file = e.target.files?.[0];
        if (!file) return;
        void ingestFileForCurrentTab(file, currentProjectId, activeTab);
        e.target.value = "";
    };

    const openCreateProjectDialog = () => {
        setProjectNameDraft("");
        setProjectEmailSlugDraft("");
        setProjectSlugEdited(false);
        setProjectSetupError(null);
        setIsProjectSetupOpen(true);
    };

    const closeCreateProjectDialog = () => {
        if (isCreatingProject) return;
        setIsProjectSetupOpen(false);
        setProjectSetupError(null);
    };

    const handleProjectNameDraftChange = (value: string) => {
        setProjectNameDraft(value);
        setProjectSetupError(null);

        if (!projectSlugEdited) {
            setProjectEmailSlugDraft(suggestEmailSlug(value));
        }
    };

    const handleProjectEmailSlugDraftChange = (value: string) => {
        setProjectSlugEdited(true);
        setProjectSetupError(null);
        setProjectEmailSlugDraft(normalizeEmailSlug(value));
    };

    const handleCreateProject = async (event?: React.FormEvent) => {
        event?.preventDefault();
        if (!canUseOrgScopedApi) return;

        const trimmed = projectNameDraft.trim();
        if (!trimmed) {
            setProjectSetupError("Project name is required.");
            return;
        }

        const emailSlug = normalizeEmailSlug(projectEmailSlugDraft || suggestEmailSlug(trimmed));
        if (!EMAIL_SLUG_PATTERN.test(emailSlug)) {
            setProjectSetupError("Email slug must be 3-40 characters and use only letters, numbers, and hyphens.");
            return;
        }

        setIsCreatingProject(true);
        setProjectsError(null);
        setProjectSetupError(null);

        try {
            const json: CreateProjectApiResponse = await api.post(
                "/projects",
                { name: trimmed, emailSlug },
            );

            if (!json.ok || !json.project) {
                throw new Error(json.error ?? "Failed to create project");
            }

            const newProjectId = json.project.id;

            // Reload full list from backend so summaries stay accurate
            await loadProjects();

            // Select the newly created project
            console.log("[ProjectSourcesPage] Created new project, calling markStepComplete('project')");
            setCurrentProjectId(newProjectId);
            setSearchParams({ projectId: newProjectId, tab: activeTab });
            setIsProjectSetupOpen(false);
            markStepComplete("project");
        } catch (err: any) {
            setProjectSetupError(err?.message ?? "Failed to create project");
        } finally {
            setIsCreatingProject(false);
        }
    };

    const ingestFileForCurrentTab = async (
        file: File,
        projectId: string,
        tab: SourcesTabKey,
    ) => {
        if (!canUseOrgScopedApi || !orgId) return;

        // Track whether the user explicitly chose to replace the baseline
        let replaceExistingBaseline = false;

        if (tab === "scopes") {
            const hasBaseline = documents.some((d) => d.type === "baseline");

            if (hasBaseline) {
                const confirmed = window.confirm(
                    "This project already has a baseline scope.\n\n" +
                    "Replacing it will archive the current baseline and mark existing flags as stale.\n\n" +
                    "Do you want to replace the baseline?"
                );

                if (!confirmed) {
                    // User backed out – don't ingest anything
                    return;
                }

                replaceExistingBaseline = true;
                // Clear any prior error message
                setIngestError(null);
            }
        }


        setIsIngesting(true);
        setIngestError(null);

        try {
            const text = await file.text();
            const types = tabToDocumentTypes[tab];
            const documentType = types[0];

            const title = deriveTitleFromFileName(file.name) || "Uploaded document";

            const body = {
                orgId,
                projectId,
                title,
                content: text,
                type: documentType,
                source: "upload",
                // Only matters for baseline uploads; API ignores it otherwise
                replaceExistingBaseline,
            };

            const json = await api.post("/ingest/document", body);

            if (!json.ok) {
                throw new Error(
                    (json as any)?.error ?? "Failed to ingest document",
                );
            }

            // Onboarding: baseline vs project docs
            if (documentType === "baseline") {
                markStepComplete("baseline");
            } else {
                markStepComplete("documents");
            }

            if (currentProjectId) {
                void loadDocuments(currentProjectId);
            }
        } catch (err: any) {
            setIngestError(err?.message || "Failed to ingest document");
        } finally {
            setIsIngesting(false);
        }
    };

    const handleAnalyzeClick = async () => {
        if (!canUseOrgScopedApi || !currentProjectId) return;

        setIsRunningAnalysis(true);
        setAnalysisError(null);

        try {
            const json = await api.post(
                `/projects/${currentProjectId}/vigilo/run`,
            );

            if (!json.ok) {
                throw new Error(
                    (json as any)?.error ?? "Failed to run analysis",
                );
            }

            // Onboarding: analysis complete
            markStepComplete("analysis");

            // On success, go to Workbench for this project
            navigate(`/workbench?projectId=${currentProjectId}`);
        } catch (err: any) {
            setAnalysisError(err?.message ?? "Failed to run analysis");
        } finally {
            setIsRunningAnalysis(false);
        }
    };

    // NEW: toggle exclude/include for a single document
    const handleToggleExclude = async (doc: ProjectDocument) => {
        if (!canUseOrgScopedApi || !currentProjectId) return;

        setExclusionError(null);

        const action = doc.excluded ? "include" : "exclude";

        try {
            const json = await api.post(`/documents/${doc.id}/${action}`);

            if (!json.ok) {
                throw new Error(
                    (json as any)?.error ??
                    `Failed to ${action} document from analysis`,
                );
            }

            // ✅ Optimistic update so pills flip instantly
            setDocuments((prev) =>
                prev.map((d) =>
                    d.id === doc.id ? { ...d, excluded: !doc.excluded } : d,
                ),
            );

            // ✅ Still reload from server to stay source-of-truth accurate
            void loadDocuments(currentProjectId);
        } catch (err: any) {
            setExclusionError(
                err?.message || "Failed to update document analysis status",
            );
        }
    };

    const currentProject =
        projects.find((p) => p.id === currentProjectId) ?? null;

    const baselineDocuments = documents.filter((d) => d.type === "baseline");
    const hasBaseline = baselineDocuments.length > 0;

    // Only count non-excluded comparison docs
    const hasComparison = documents.some(
        (d) => d.type && d.type !== "baseline" && !d.excluded,
    );

    const docsForTab = filterDocumentsForTab(documents, activeTab);

    // Canonical analysis state
    const hasProject = !!currentProjectId;
    const canAnalyze =
        hasProject && hasBaseline && hasComparison && !isRunningAnalysis;

    // Tooltip for disabled states (empty when enabled)
    let analyzeTooltip = "";
    if (!hasProject) {
        analyzeTooltip = "Select a project to begin.";
    } else if (!hasBaseline) {
        analyzeTooltip = "Upload a baseline scope to compare against.";
    } else if (!hasComparison) {
        analyzeTooltip =
            "Include at least one project document, email, or note in analysis.";
    }


    return (
        <AppLayout
            projects={projects}
            currentProjectId={currentProjectId}
            onProjectChange={handleProjectChange}
            onCreateProject={openCreateProjectDialog}
            isCreatingProject={isCreatingProject}
        >
            {isProjectSetupOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 px-4">
                    <form
                        onSubmit={handleCreateProject}
                        className="w-full max-w-lg rounded-lg border border-slate-700 bg-slate-950 p-5 shadow-2xl shadow-slate-950"
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-base font-semibold text-slate-50">
                                    New project
                                </h2>
                                <p className="mt-1 text-xs text-slate-400">
                                    Choose a forwarding slug that will be easy to recognize in Outlook autocomplete.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeCreateProjectDialog}
                                disabled={isCreatingProject}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-800 bg-slate-900 text-slate-400 hover:border-slate-600 hover:text-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                                title="Close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="mt-5 space-y-4">
                            <label className="block">
                                <span className="text-xs font-medium text-slate-300">
                                    Project name
                                </span>
                                <input
                                    type="text"
                                    value={projectNameDraft}
                                    onChange={(event) => handleProjectNameDraftChange(event.target.value)}
                                    className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                                    placeholder="Ridgeline Commons"
                                    maxLength={200}
                                    autoFocus
                                />
                            </label>

                            <label className="block">
                                <span className="text-xs font-medium text-slate-300">
                                    Email slug
                                </span>
                                <input
                                    type="text"
                                    value={projectEmailSlugDraft}
                                    onChange={(event) => handleProjectEmailSlugDraftChange(event.target.value)}
                                    className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 font-mono text-sm text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                                    placeholder="ridgeline"
                                    maxLength={EMAIL_SLUG_MAX_LENGTH}
                                />
                                <p className="mt-1 text-[11px] leading-5 text-slate-500">
                                    Use 3-40 lowercase letters, numbers, and hyphens. Make it descriptive enough to identify when forwarding email later.
                                </p>
                            </label>

                            <div className="rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2">
                                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                    Address preview
                                </div>
                                <code className="mt-1 block break-all text-xs text-slate-300">
                                    p_{normalizeEmailSlug(projectEmailSlugDraft || suggestEmailSlug(projectNameDraft)) || "project"}_7gx4mb9q2kfc@inbound.scopeshield.app
                                </code>
                            </div>

                            {projectSetupError && (
                                <div className="rounded-md border border-red-500/40 bg-red-950/30 px-3 py-2 text-xs text-red-200">
                                    {projectSetupError}
                                </div>
                            )}
                        </div>

                        <div className="mt-5 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={closeCreateProjectDialog}
                                disabled={isCreatingProject}
                                className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-slate-500 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isCreatingProject || !canUseOrgScopedApi}
                                className="rounded-md bg-amber-500 px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-amber-400 disabled:cursor-not-allowed disabled:bg-slate-800 disabled:text-slate-500"
                            >
                                {isCreatingProject ? "Creating..." : "Create project"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="px-8 py-6 space-y-6">
                {/* Header + global Analyze button */}
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-xl font-semibold text-slate-50">
                            Sources
                        </h1>
                        <p className="mt-1 text-sm text-slate-400">
                            Add scopes, contracts, project docs, and
                            communication so ScopeShield can watch the project
                            for scope creep.
                        </p>
                        {currentProject && (
                            <>
                                <p className="mt-1 text-xs text-slate-500">
                                    Project: {currentProject.name}
                                </p>

                                {/* Privacy Gate Note */}
                                <div className="mt-2 flex items-start gap-2 text-[11px] text-slate-400">
                                    <span>
                                        <span className="font-semibold text-slate-300">
                                            Privacy Gate is active.
                                        </span>{" "}
                                        Emails, phone numbers, and other
                                        identifiers are anonymized before AI
                                        processing. Your original documents stay
                                        securely in ScopeShield.
                                    </span>
                                </div>
                            </>
                        )}

                        {enrollmentError && (
                            <div className="mt-2 rounded-lg border border-red-500/40 bg-red-950/30 px-4 py-3 text-sm text-red-100">
                                <strong className="font-semibold">Enrollment failed:</strong> {enrollmentError}
                                <p className="mt-1 text-xs text-red-200">
                                    Please refresh the page or contact support if this persists.
                                </p>
                            </div>
                        )}

                        {projectsError && (
                            <p className="mt-2 text-xs text-red-300">
                                {projectsError}
                            </p>
                        )}

                        {analysisError && (
                            <p className="mt-2 text-xs text-red-300">
                                {analysisError}
                            </p>
                        )}
                        {hasBaseline && !hasComparison && (
                            <p className="mt-2 text-[11px] text-slate-400">
                                Baseline scope is set. Include at least one
                                project document, email, or note in analysis to
                                compare against it.
                            </p>
                        )}
                        {!hasBaseline && documents.length > 0 && (
                            <p className="mt-2 text-[11px] text-amber-300">
                                Documents are present, but no baseline scope is
                                marked. Add a scope in the Scopes &amp;
                                Contracts tab for best results.
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={openCreateProjectDialog}
                            disabled={isCreatingProject || !canUseOrgScopedApi}
                            className={[
                                "inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold",
                                isCreatingProject || !canUseOrgScopedApi
                                    ? "bg-slate-800 text-slate-500 cursor-not-allowed"
                                    : "bg-slate-900 text-slate-100 border border-slate-700 hover:border-amber-500 hover:bg-slate-800",
                            ].join(" ")}
                        >
                            {isCreatingProject ? "Creating…" : "New project"}
                        </button>

                        <button
                            type="button"
                            onClick={handleAnalyzeClick}
                            disabled={!canAnalyze}
                            title={!canAnalyze ? analyzeTooltip : ""}
                            className={[
                                "inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold",
                                canAnalyze
                                    ? "bg-amber-500 text-slate-900 hover:bg-amber-400"
                                    : "bg-slate-800 text-slate-500 cursor-not-allowed",
                            ].join(" ")}
                        >
                            {isRunningAnalysis ? "Analyzing…" : "Analyze with ScopeShield"}
                        </button>
                    </div>
                </div>

                {/* Runtime configuration status */}
                {!hasApiBaseUrl && (
                    <div className="rounded-lg border border-red-500/40 bg-red-950/30 px-4 py-3 text-sm text-red-100">
                        Missing API configuration: VITE_API_BASE_URL.
                    </div>
                )}
                {hasApiBaseUrl && !orgId && !enrollmentError && (
                    <div className="rounded-lg border border-slate-500/40 bg-slate-950/30 px-4 py-3 text-sm text-slate-100">
                        Setting up your organization…
                    </div>
                )}

                {/* Tabs */}
                <div className="border-b border-slate-800">
                    <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                        {(
                            ["scopes", "project_docs", "emails_notes"] as SourcesTabKey[]
                        ).map((tab) => {
                            const isActive = activeTab === tab;
                            return (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => {
                                        setActiveTab(tab);
                                        setSearchParams({
                                            projectId: currentProjectId ?? "",
                                            tab,
                                        });
                                    }}
                                    className={[
                                        "relative whitespace-nowrap border-b-2 px-3 pb-2 text-sm font-medium transition-colors",
                                        isActive
                                            ? "border-amber-500 text-amber-200 bg-slate-900/80"
                                            : "border-transparent text-slate-400 hover:border-slate-600 hover:text-slate-100",
                                    ].join(" ")}
                                >
                                    {mapTabToLabel[tab]}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Drag + drop ingest panel */}
                <div className="grid grid-cols-3 gap-6">
                    <div className="col-span-1">
                        <div
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            className={[
                                "flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition",
                                isDragging
                                    ? "border-amber-400 bg-amber-500/10"
                                    : "border-slate-700 bg-slate-950/60 hover:border-amber-500/60 hover:bg-slate-900/60",
                            ].join(" ")}
                        >
                            <p className="text-sm font-medium text-slate-50">
                                {mapTabToLabel[activeTab]}
                            </p>
                            <p className="mt-2 text-xs text-slate-400">
                                {mapTabToHelperText[activeTab]}
                            </p>

                            <div className="mt-4 text-[11px] text-slate-500">
                                <p>Drop a file here, or</p>
                                <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-[11px] font-medium text-slate-100 hover:border-amber-500 hover:bg-slate-800">
                                    <span>
                                        {isIngesting ? "Uploading…" : "Browse files"}
                                    </span>
                                    <input
                                        type="file"
                                        className="hidden"
                                        onChange={handleFileInputChange}
                                        disabled={
                                            isIngesting || !currentProjectId
                                        }
                                    />
                                </label>
                            </div>
                            {!currentProjectId && (
                                <p className="mt-3 text-[11px] text-amber-300">
                                    Select a project first to enable uploads.
                                </p>
                            )}
                            {ingestError && (
                                <p className="mt-3 text-[11px] text-red-300">
                                    {ingestError}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Right: documents for the active tab */}
                    <div className="col-span-2">
                        <div className="rounded-xl border border-slate-800 bg-slate-900/60">
                            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
                                <span className="text-sm font-medium text-slate-200">
                                    {mapTabToLabel[activeTab]} for this project
                                </span>
                                {activeTab === "emails_notes" && ingestAddress && (
                                    <div className="flex items-center gap-2">
                                        <Mail className="h-3.5 w-3.5 text-slate-500" />
                                        <code className="text-[11px] text-slate-400">
                                            {ingestAddress}
                                        </code>
                                        <button
                                            type="button"
                                            onClick={handleCopyAddress}
                                            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-300 hover:border-amber-500 hover:text-amber-400 transition"
                                            title="Copy email address"
                                        >
                                            {copiedAddress ? (
                                                <><Check className="h-3 w-3" /> Copied</>
                                            ) : (
                                                <><Copy className="h-3 w-3" /> Copy</>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="max-h-[360px] overflow-auto px-4 py-3">
                                {isLoadingDocuments && (
                                    <div className="flex h-24 items-center justify-center text-xs text-slate-400">
                                        Loading documents…
                                    </div>
                                )}

                                {documentsError && !isLoadingDocuments && (
                                    <div className="rounded-md border border-red-500/40 bg-red-950/30 px-3 py-2 text-xs text-red-200">
                                        {documentsError}
                                    </div>
                                )}

                                {exclusionError && !isLoadingDocuments && (
                                    <div className="mt-2 rounded-md border border-red-500/40 bg-red-950/30 px-3 py-2 text-xs text-red-200">
                                        {exclusionError}
                                    </div>
                                )}

                                {!isLoadingDocuments &&
                                    !documentsError &&
                                    docsForTab.length === 0 && (
                                        <div className="rounded-md border border-slate-700 bg-slate-900/40 px-3 py-3 text-xs text-slate-400">
                                            No documents found for this tab
                                            yet.
                                        </div>
                                    )}

                                {!isLoadingDocuments &&
                                    !documentsError &&
                                    docsForTab.length > 0 && (
                                        <table className="min-w-full divide-y divide-slate-800 text-left text-xs">
                                            <thead>
                                                <tr>
                                                    <th className="px-2 py-2 font-medium text-slate-400">
                                                        Title
                                                    </th>
                                                    <th className="px-2 py-2 font-medium text-slate-400">
                                                        Type
                                                    </th>
                                                    <th className="px-2 py-2 font-medium text-slate-400">
                                                        Evidence
                                                    </th>
                                                    <th className="px-2 py-2 font-medium text-slate-400">
                                                        Body
                                                    </th>
                                                    <th className="px-2 py-2 font-medium text-slate-400">
                                                        Created
                                                    </th>
                                                    {/* NEW: analysis status/actions */}
                                                    <th className="px-2 py-2 font-medium text-slate-400">
                                                        Analysis
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-800/80">
                                                {docsForTab.map((doc) => {
                                                    const rowClasses = [
                                                        "px-2 py-1 text-xs",
                                                        doc.excluded
                                                            ? "text-slate-500"
                                                            : "text-slate-100",
                                                    ].join(" ");

                                                    return (
                                                        <tr key={doc.id}>
                                                            <td className={rowClasses}>
                                                                {doc.title}
                                                            </td>
                                                            <td className="px-2 py-1 text-[11px] text-slate-300">
                                                                {doc.type ??
                                                                    "—"}
                                                            </td>
                                                            <td className="px-2 py-1 text-[11px] text-slate-300">
                                                                {
                                                                    doc.evidenceCount
                                                                }
                                                            </td>
                                                            <td className="px-2 py-1 text-[11px] text-slate-300">
                                                                {doc.hasBody
                                                                    ? "Yes"
                                                                    : "No"}
                                                            </td>
                                                            <td className="px-2 py-1 text-[11px] text-slate-400">
                                                                {doc.createdAt
                                                                    ? new Date(
                                                                        doc.createdAt,
                                                                    ).toLocaleString()
                                                                    : "—"}
                                                            </td>
                                                            <td className="px-2 py-1 text-[11px] text-slate-300">
                                                                <div className="flex items-center gap-2">
                                                                    <span
                                                                        className={
                                                                            doc.excluded
                                                                                ? "rounded-full bg-slate-800 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-300"
                                                                                : "rounded-full bg-amber-900/40 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-300"
                                                                        }
                                                                    >
                                                                        {doc.excluded
                                                                            ? "Excluded"
                                                                            : "Included"}
                                                                    </span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            handleToggleExclude(
                                                                                doc,
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            isLoadingDocuments ||
                                                                            isIngesting ||
                                                                            isRunningAnalysis
                                                                        }
                                                                        className="rounded-md border border-slate-700 bg-slate-900 px-2 py-0.5 text-[10px] font-medium text-slate-100 hover:border-amber-500 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                                                                    >
                                                                        {doc.excluded
                                                                            ? "Include in analysis"
                                                                            : "Exclude from analysis"}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

export default ProjectSourcesPage;
